import React, { useState } from 'react';
import { Form, Formik } from 'formik';
import { Button } from 'react-bootstrap';
import * as Yup from 'yup';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import homepageApi from '../../api/homepage';
import CustomInput from '../../common/FormComponents/Input';
import CustomModal from '../../common/UI/CustomModal';
import DeleteConfirmationModal from '../../common/UI/CustomModal/deleteModal';
import { toastError, toastSuccess } from '../../common/UI/Toasts';
import { getFormDataFromObject } from '../../utils/helpers';
import ReactSwitch from 'react-switch';
import editIcon from '../../assets/images/pencil.svg';
import deleteIcon from '../../assets/images/trash.svg';
import Loader from '../../common/UI/Loader';
import ThemeEditor from './ThemeEditor';

const validationSchema = Yup.object({
  title: Yup.string().required('Question is required'),
  description: Yup.string().required('Answer is required'),
});

const FAQManager = ({ section, backToList, refreshSections }) => {
  const [items, setItems] = useState(section.items || []);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [showThemeEditor, setShowThemeEditor] = useState(false);

  const sortedItems = [...items].sort((a, b) => a.order - b.order);

  const handleAddNew = () => {
    setEditingItem(null);
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const handleDelete = (itemId) => {
    setDeleteItemId(itemId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setLoading(true);
    try {
      await homepageApi.deleteItemFromSection(section.sectionKey, deleteItemId);
      toastSuccess('FAQ deleted successfully');
      refreshSections();
      fetchItems();
    } catch (error) {
      toastError('Failed to delete FAQ');
      console.error(error);
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
      setDeleteItemId(null);
    }
  };

  const fetchItems = async () => {
    try {
      const response = await homepageApi.getHomepageSection(section.sectionKey);
      setItems(response?.data?.data?.items || []);
    } catch (error) {
      console.error(error);
    }
  };

  const handleToggleStatus = async (itemId, currentStatus) => {
    setLoading(true);
    try {
      const payload = getFormDataFromObject({ status: !currentStatus });
      await homepageApi.updateItemInSection(section.sectionKey, itemId, payload);
      toastSuccess('Status updated successfully');
      fetchItems();
    } catch (error) {
      toastError('Failed to update status');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (values, { resetForm }) => {
    setLoading(true);
    try {
      let payload = {
        title: values.title,
        description: values.description,
        order: values.order || 0,
        status: values.status !== undefined ? values.status : true,
      };

      payload = getFormDataFromObject(payload);

      if (editingItem) {
        await homepageApi.updateItemInSection(section.sectionKey, editingItem._id, payload);
        toastSuccess('FAQ updated successfully');
      } else {
        await homepageApi.addItemToSection(section.sectionKey, payload);
        toastSuccess('FAQ added successfully');
        resetForm();
      }

      setShowModal(false);
      setEditingItem(null);
      fetchItems();
      refreshSections();
    } catch (error) {
      toastError(editingItem ? 'Failed to update FAQ' : 'Failed to add FAQ');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const reorderedItems = Array.from(sortedItems);
    const [removed] = reorderedItems.splice(result.source.index, 1);
    reorderedItems.splice(result.destination.index, 0, removed);

    const updatedItems = reorderedItems.map((item, index) => ({
      _id: item._id,
      order: index,
    }));

    setItems(reorderedItems.map((item, index) => ({ ...item, order: index })));

    try {
      await homepageApi.reorderItems(section.sectionKey, { items: updatedItems });
      toastSuccess('Order updated successfully');
    } catch (error) {
      toastError('Failed to update order');
      fetchItems();
    }
  };

  if (loading) {
    return <Loader loading={loading} />;
  }

  return (
    <div className="content_wrapper all_products_main">
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12">
            <ol className="breadcrumb">
              <li onClick={backToList} style={{ cursor: 'pointer' }}>
                Landing Page CMS
              </li>
              <li>FAQ Management</li>
            </ol>
          </div>
        </div>

        <div className="row">
          <div className="col-md-12">
            <div className="box_main">
              <div className="header_top">
                <h3>FAQ Management</h3>
              </div>
              <div className="middle_main">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div className="d-flex gap-2">
                    <Button variant="outline-primary" onClick={() => setShowThemeEditor(true)}>
                      <i className="fa fa-palette me-2"></i>Theme
                    </Button>
                    <Button variant="primary" onClick={handleAddNew}>
                      Add New FAQ
                    </Button>
                  </div>
                  <Button variant="secondary" onClick={backToList}>
                    Back to Sections
                  </Button>
                </div>

                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="faq">
                    {(provided) => (
                      <div {...provided.droppableProps} ref={provided.innerRef}>
                        {sortedItems.map((item, index) => (
                          <Draggable key={item._id} draggableId={item._id} index={index}>
                            {(provided) => (
                              <div
                                className="card mb-3"
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <div className="card-body">
                                  <div className="d-flex justify-content-between align-items-start">
                                    <div style={{ flex: 1 }}>
                                      <h5
                                        className="card-title mb-2"
                                        style={{ cursor: 'pointer' }}
                                        onClick={() =>
                                          setExpandedIndex(expandedIndex === index ? null : index)
                                        }
                                      >
                                        {expandedIndex === index ? '▼' : '▶'} {item.title}
                                      </h5>
                                      {expandedIndex === index && (
                                        <p className="card-text text-muted">{item.description}</p>
                                      )}
                                    </div>
                                    <div className="d-flex align-items-center ms-3">
                                      <ReactSwitch
                                        uncheckedIcon={false}
                                        checkedIcon={false}
                                        onChange={() => handleToggleStatus(item._id, item.status)}
                                        checked={item.status}
                                        height={20}
                                        width={40}
                                        className="me-3"
                                      />
                                      <span
                                        className="me-2"
                                        onClick={() => handleEdit(item)}
                                        style={{ cursor: 'pointer' }}
                                      >
                                        <img src={editIcon} alt="Edit" width="24" height="24" />
                                      </span>
                                      <span
                                        onClick={() => handleDelete(item._id)}
                                        style={{ cursor: 'pointer' }}
                                      >
                                        <img src={deleteIcon} alt="Delete" width="24" height="24" />
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CustomModal
        title={editingItem ? 'Edit FAQ' : 'Add New FAQ'}
        show={showModal}
        handleToggle={() => setShowModal(false)}
        size="lg"
      >
        <Formik
          initialValues={{
            title: editingItem?.title || '',
            description: editingItem?.description || '',
            order: editingItem?.order || 0,
            status: editingItem ? editingItem.status : true,
          }}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
          enableReinitialize
        >
          {(formik) => (
            <Form>
              <div className="px-3">
                <CustomInput
                  name="title"
                  label="Question"
                  placeholder="How does it work?"
                  labelclassname="form-label"
                  className="form-control mb-3"
                  isRequired
                />

                <div className="mb-3">
                  <label className="form-label">Answer *</label>
                  <textarea
                    name="description"
                    placeholder="Our process is simple..."
                    className="form-control"
                    rows="5"
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.description && formik.errors.description && (
                    <div className="text-danger small">{formik.errors.description}</div>
                  )}
                </div>

                <CustomInput
                  name="order"
                  label="Order"
                  type="number"
                  placeholder="0"
                  labelclassname="form-label"
                  className="form-control mb-3"
                />

                <div className="mb-3">
                  <label className="form-label">Status</label>
                  <div>
                    <ReactSwitch
                      uncheckedIcon={false}
                      checkedIcon={false}
                      onChange={(checked) => formik.setFieldValue('status', checked)}
                      checked={formik.values.status}
                      height={20}
                      width={40}
                    />
                  </div>
                </div>
              </div>

              <div className="footer_main mt-4">
                <Button
                  type="button"
                  variant="secondary"
                  className="me-2"
                  onClick={() => {
                    setShowModal(false);
                    setEditingItem(null);
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" variant="primary" disabled={loading}>
                  {loading ? 'Saving...' : editingItem ? 'Update' : 'Save'}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </CustomModal>

      <DeleteConfirmationModal
        name="FAQ"
        show={showDeleteModal}
        handleToggle={() => setShowDeleteModal(false)}
        handleDelete={confirmDelete}
      />

      <ThemeEditor
        show={showThemeEditor}
        onHide={() => setShowThemeEditor(false)}
        sectionKey={section.sectionKey}
        sectionName="FAQ"
      />
    </div>
  );
};

export default FAQManager;
